import { AfterContentChecked, AfterViewInit, ChangeDetectorRef, Component, Input, OnInit, ViewChild } from '@angular/core';

import { finalize } from 'rxjs';

import { TwcPopupComponent, TwcWizardComponent } from '@topcon/twc-angular';

import { JobSiteService, ToastService } from '@mirage-projects/cw-commons';
import {
    CatalogObject,
    DESIGN_DATA_JOB_TYPE,
    GCP_JOB_TYPE,
    JobSite,
    JobSiteReferenceData,
    PcvizUploadUrlInfo,
    ProjectDataEnum,
    ProjectType,
} from '@mirage-projects/cw-models';

import { UploadWizardService } from '../../services/upload-wizard-service/upload-wizard.service';
import { CreateProjectContentComponent } from '../create-project-dialog/create-project-content/create-project-content.component';
import { CreateWizardComponent } from '../create-wizard/create-wizard.component';
import { ObjectBrowserSelectionService } from '../object-browser/selection.service';
import { AddDataOptionTypeData } from './project-layer-option';

/**
 * Component to add a new project to the site
 */
@Component({
    selector: 'cw-add-data',
    templateUrl: './add-data.component.html',
    styleUrls: ['./add-data.component.scss'],
    standalone: false,
})
export class AddDataComponent implements OnInit, AfterViewInit, AfterContentChecked {
    /** JobSite Id of the jobsite to append the project to */
    @Input() jobSite: JobSite | null = null;
    /** Go to one of the pages directly for single page mode (currently only tested with existing_project) */
    @Input() gotoPage!: AddDataOptionTypeData['type'];
    @ViewChild(CreateProjectContentComponent) createProjectContentComponent!: CreateProjectContentComponent;
    @ViewChild(CreateWizardComponent) createWizardComponent!: CreateWizardComponent | undefined;
    @ViewChild(TwcPopupComponent) popup!: TwcPopupComponent;
    @ViewChild(TwcWizardComponent) wizard!: TwcWizardComponent;

    isLoading = false;
    isFirstPage = true;
    isCreateProject = false;
    isUploadWizard = false;
    isExisting = false;
    isNewLayer = false;
    hasNextButton = true;

    projectType: ProjectType | '' = '';
    selectedDataType: AddDataOptionTypeData | null = null;

    selectedExistingProject: CatalogObject | null = null;

    constructor(
        public uploadWizardService: UploadWizardService,
        private jobSiteService: JobSiteService,
        private selectionService: ObjectBrowserSelectionService,
        private cdr: ChangeDetectorRef,
        private toastService: ToastService
    ) {}

    ngOnInit(): void {
        if (this.gotoPage) {
            this.selectedDataType = { type: this.gotoPage };
            this.onNext();
        }
    }

    ngAfterViewInit(): void {
        this.wizard.options ??= {};
        this.wizard.options.data ??= {};
        this.wizard.options.data = { ...this.wizard.options.data, importType: 'project' };
    }

    // To fix ExpressionChangedAfterItHasBeenCheckedError when selecting the wizard option on add project dialog
    ngAfterContentChecked(): void {
        this.cdr.detectChanges();
    }

    /**
     * Set the type of project to create
     * @param typeData Type of project to create
     */
    setSelectedType(typeData: AddDataOptionTypeData): void {
        this.selectedDataType = typeData;
    }

    /**
     * Next button action in wizard create workflow
     */
    onWizardNext(): void {
        if (this.isUploadWizard || this.isNewLayer) {
            const uploadWizardStep = this.wizard?.selectedIndex - 1;
            this.uploadWizardService.initStepWizard(uploadWizardStep);
        }
        this.wizard.nextStep();
    }

    /**
     * Next button action
     */
    onNext(): void {
        if (!this.selectedDataType) {
            return;
        }

        const { type, projectType } = this.selectedDataType;

        this.isCreateProject = false;
        this.isUploadWizard = false;
        this.isExisting = false;
        this.isNewLayer = false;

        switch (type) {
            case 'new_project':
                this.projectType = projectType ?? '';
                this.isCreateProject = true;
                break;
            case 'create_wizard':
                this.isUploadWizard = true;
                this.wizard.options.data.importType = 'project';
                this.wizard.options.data.selectFirstJobAdapter = true;

                /** Init upload wizard service */
                this.uploadWizardService.init(this.wizard);
                this.uploadWizardService.jobSiteId = this.jobSite?.id;
                this.uploadWizardService.stepChanged = this.wizard.stepChanged;
                break;
            case 'existing_project':
                this.isExisting = true;
                this.selectionService.resetSelection();
                break;
            case 'new_layer':
                this.isNewLayer = true;
                this.wizard.options.data.importType = 'ImportJobSiteRefs';
                this.wizard.options.data.selectFirstJobAdapter = false;
                this.uploadWizardService.init(this.wizard);
                this.uploadWizardService.jobSiteId = this.jobSite?.id;
                this.uploadWizardService.stepChanged = this.wizard.stepChanged;
                break;
            default:
                break;
        }

        this.hasNextButton = false;
        this.isFirstPage = false;

        this.onWizardNext();
    }

    /**
     * Previous button action
     */
    onPrevious(): void {
        if ((this.isUploadWizard || this.isNewLayer) && this.wizard.selectedIndex !== 1) {
            this.wizard.prevStep();
            return;
        }

        this.isCreateProject = false;
        this.isUploadWizard = false;
        this.isExisting = false;
        this.isNewLayer = false;
        this.hasNextButton = true;
        this.isFirstPage = true;
        this.selectedExistingProject = null;
        this.selectedDataType = null;

        this.wizard.prevStep();
    }

    /**
     * Create button action
     */
    onCreate(): void {
        if (!this.selectedDataType) {
            return;
        }

        const { type } = this.selectedDataType;

        switch (type) {
            case 'new_project':
                this.createProjectContentComponent.onCreateProject();
                return;
            case 'existing_project':
                this.addExistingProject();
                return;

            default:
                return;
        }
    }

    /**
     * Loading spinner visibility state
     * @param isLoading Set loading state
     */
    setLoading(isLoading: boolean): void {
        this.isLoading = isLoading;
    }

    /* istanbul ignore next*/
    onUploadButtonClick(): void {
        this.uploadWizardService.uploadAndProcess(this.jobSite?.id);
    }

    /**
     * Creates and uploads the reference data
     *
     */
    async createAndUploadReferenceData(): Promise<void> {
        this.isLoading = true;

        try {
            const { object, upload, formatType, jobAdapter } = await this.uploadWizardService.createAndUploadJobSiteReferenceData();
            if (!object || !object.id || !upload || !formatType) {
                this.toastService.error('JOB_SITE.ADD_LAYER.ERROR');
                return;
            }

            const layer: JobSiteReferenceData = {
                layerId: object.id,
                inputFiles: this.getFilesPath(upload.urls) ?? [],
                dataType: formatType.type === DESIGN_DATA_JOB_TYPE ? ProjectDataEnum.designData : ProjectDataEnum.controlPoint,
                options: formatType.type === GCP_JOB_TYPE ? jobAdapter?.getImporterOptions() : undefined,
            };

            this.wizard.options.data.onLayerReady$.next(layer);
            this.wizard.onClose();
        } catch (e) {
            this.toastService.error('JOB_SITE.ADD_LAYER.ERROR');
        } finally {
            this.isLoading = false;
        }
    }

    get isUploadWizardSelected(): boolean {
        return this.selectedDataType?.type === 'create_wizard';
    }

    get isNewLayerSelected(): boolean {
        return this.selectedDataType?.type === 'new_layer';
    }

    get areNoProjectsSelected(): boolean {
        return this.selectionService.getNumberSelectedElements() < 1;
    }

    /**
     * Check if next button is enabled
     */
    get isNextEnabled(): boolean {
        return !!this.selectedDataType;
    }

    /**
     * Check if create button is enabled
     */
    get isValid(): boolean {
        if (!this.selectedDataType) {
            return false;
        }

        const { type } = this.selectedDataType;

        switch (type) {
            case 'new_project':
                return !!this.createProjectContentComponent?.createProjectForm?.valid;
            case 'create_wizard':
            case 'existing_project':
                return true;

            default:
                return false;
        }
    }

    get isSinglePage(): boolean {
        return !!this.gotoPage;
    }

    /* istanbul ignore next*/
    get isUploadStarted(): boolean {
        return this.uploadWizardService.startedUpload;
    }

    get isNextButtonVisible(): boolean {
        return !this.wizard?.isLastStep;
    }

    get isNextButtonEnabled(): boolean {
        return this.uploadWizardService.isValidStep();
    }

    get isUploadButtonVisible(): boolean {
        return this.wizard?.isLastStep && !this.uploadWizardService.startedUpload && this.isUploadWizardSelected;
    }

    get isCloseButtonVisible(): boolean {
        return this.uploadWizardService.startedUpload;
    }

    get hasFile(): boolean {
        return !!this.uploadWizardService.files.length;
    }

    get isAddLayerButtonVisible(): boolean {
        return this.wizard?.isLastStep && !this.uploadWizardService.startedUpload && this.isNewLayerSelected;
    }

    private addExistingProject(): void {
        const selectedProject = this.selectionService.getSelectedElements('projects')?.[0];
        if (this.jobSite && selectedProject?.id) {
            this.isLoading = true;
            this.jobSiteService
                .addProject(this.jobSite.id, selectedProject?.id)
                .pipe(finalize(() => (this.isLoading = false)))
                .subscribe({
                    next: () => {
                        this.wizard.options?.data?.onRefresh$?.next({
                            projectId: selectedProject.id,
                            projectType: this.projectType,
                            openViewer: false,
                        });
                        this.wizard.onClose();
                        this.toastService.generic('JOB_SITE.ADD_PROJECT.SUCCESS');
                    },
                    error: () => this.toastService.error('JOB_SITE.ADD_PROJECT.ERROR'),
                });
        }
    }

    private getFilesPath(urls: PcvizUploadUrlInfo[] | undefined): string[] | undefined {
        return urls?.map((url) => url.filePath);
    }
}
